/**
 * Phone call log API.
 *
 * One row per call. A call is either logged after the fact ('Logged') or
 * scheduled ahead of time ('Scheduled') and logged later. Either state can be
 * voided with a reason and unvoided again, which restores the status the call
 * had before it was voided.
 *
 * The contact fields are a snapshot taken when the call was created, so the log
 * still reads correctly after the client record is edited.
 *
 * `notes` is what was written when the call was logged. Later updates on a case
 * that stays open are follow-up notes, each with its author and time.
 */

import { API_BASE } from '../config/apiBase';

export type PhoneCallStatus = 'Scheduled' | 'Logged' | 'Void';

export interface PhoneCallRow {
  id: number;
  status: PhoneCallStatus;
  employee_user_id: number | null;
  employee_name: string | null;
  client_id: number | null;
  company_name: string;
  location: string | null;
  point_of_contact: string | null;
  contact_phone: string | null;
  contact_email: string | null;
  spoke_with: string | null;
  reason: string | null;
  notes: string | null;
  scheduled_at: string | null;
  occurred_at: string | null;
  void_reason: string | null;
  created_by_user_id: number | null;
  created_by_username: string | null;
  logged_at: string | null;
  voided_at: string | null;
  created_at: string | null;
  updated_at: string | null;
  /** Number of follow-up notes; only set on rows from the call list. */
  note_count?: number;
}

export interface PhoneCallNoteRow {
  id: number;
  call_id: number;
  user_id: number | null;
  note: string;
  submitted_by: string | null;
  created_at: string;
  updated_at: string;
}

export interface PhoneCallListResponse {
  scheduled: PhoneCallRow[];
  logged: PhoneCallRow[];
  void: PhoneCallRow[];
}

async function jsonOrThrow<T>(res: Response, fallback: string): Promise<T> {
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error((data as { error?: string }).error || fallback);
  return data as T;
}

export async function fetchPhoneCalls(): Promise<PhoneCallListResponse> {
  const res = await fetch(`${API_BASE}/api/phone-calls`);
  return jsonOrThrow(res, 'Failed to fetch calls');
}

export interface CreatePhoneCallPayload {
  user_id: number;
  /** 'Logged' records a call that already happened; 'Scheduled' books one. */
  status: 'Logged' | 'Scheduled';
  employee_user_id: number;
  client_id?: number | null;
  company_name?: string;
  location?: string | null;
  point_of_contact?: string | null;
  contact_phone?: string | null;
  contact_email?: string | null;
  spoke_with?: string | null;
  reason?: string | null;
  notes?: string | null;
  scheduled_at?: string | null;
  occurred_at?: string | null;
}

export async function createPhoneCall(payload: CreatePhoneCallPayload): Promise<PhoneCallRow> {
  const res = await fetch(`${API_BASE}/api/phone-calls`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  return jsonOrThrow(res, 'Failed to save call');
}

export interface UpdatePhoneCallPayload {
  status?: PhoneCallStatus;
  employee_user_id?: number | null;
  client_id?: number | null;
  company_name?: string;
  location?: string | null;
  point_of_contact?: string | null;
  contact_phone?: string | null;
  contact_email?: string | null;
  spoke_with?: string | null;
  reason?: string | null;
  notes?: string | null;
  scheduled_at?: string | null;
  occurred_at?: string | null;
  void_reason?: string | null;
  /** Restores the status the call had before it was voided. */
  unvoid?: boolean;
}

export async function updatePhoneCall(id: number, payload: UpdatePhoneCallPayload): Promise<PhoneCallRow> {
  const res = await fetch(`${API_BASE}/api/phone-calls/${id}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  return jsonOrThrow(res, 'Failed to update call');
}

/** Confirm a scheduled call was made, optionally recording who was spoken to and notes. */
export function logScheduledCall(
  id: number,
  payload: { occurred_at?: string | null; spoke_with?: string | null; notes?: string | null }
): Promise<PhoneCallRow> {
  return updatePhoneCall(id, { status: 'Logged', ...payload });
}

export function voidPhoneCall(id: number, reason: string): Promise<PhoneCallRow> {
  return updatePhoneCall(id, { status: 'Void', void_reason: reason });
}

export function unvoidPhoneCall(id: number): Promise<PhoneCallRow> {
  return updatePhoneCall(id, { unvoid: true });
}

// ── Follow-up notes ──

export async function fetchPhoneCallNotes(id: number): Promise<PhoneCallNoteRow[]> {
  const res = await fetch(`${API_BASE}/api/phone-calls/${id}/notes`);
  const data = await jsonOrThrow<{ notes: PhoneCallNoteRow[] }>(res, 'Failed to load notes');
  return data.notes || [];
}

export async function addPhoneCallNote(id: number, note: string, userId: number): Promise<PhoneCallNoteRow> {
  const res = await fetch(`${API_BASE}/api/phone-calls/${id}/notes`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ note, user_id: userId }),
  });
  return jsonOrThrow(res, 'Failed to add note');
}
